import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';

import { Injectable } from '@angular/core';
import { LayoutDataService } from './layout-data.service';
import { MapDataService } from './map-data.service';
import { PlaceDataService } from './place-data.service';
import { SeriesDataService } from './series-data.service';
import { SeriesMapDataService } from './series-map-data.service';
import { SyncStatusStates } from './sync-status.service';
import { UserDataService } from './user-data.service';

export enum AppReadyStates {
  initializing = 'Initializing',
  synchronizing = 'Synchronizing',
  synchronized = 'Synchronized',
  error = 'Error',
}

@Injectable({
  providedIn: 'root',
})
export class AppReadyService {
  public appReady$: Observable<boolean>; // all api services have sync'd successfully ONCE
  public lastSyncAttempt$ = new BehaviorSubject<Date>(null); // last time tried
  public lastSyncSuccess$ = new BehaviorSubject<Date>(null); // last time successful
  public syncStatus$: Observable<SyncStatusStates>; // cumulative sync status
  public wasLoaded = false;

  public constructor(
    private layoutData: LayoutDataService,
    private mapData: MapDataService,
    private placeData: PlaceDataService,
    private seriesMapData: SeriesMapDataService,
    private seriesData: SeriesDataService,
    private userData: UserDataService,
  ) {
    this.appReady$ = combineLatest([
      this.layoutData.syncSuccess$,
      this.mapData.syncSuccess$,
      this.placeData.syncSuccess$,
      this.seriesData.syncSuccess$,
      this.seriesMapData.syncSuccess$,
      this.userData.syncSuccess$,
    ]).pipe(
      // all are successful
      map((apis) => {
        const ready = apis.every((api) => api);
        // console.debug({ appReady$: ready, apis });
        return ready;
      }),
      distinctUntilChanged(),
    );

    [
      this.layoutData.syncStatus$,
      this.mapData.syncStatus$,
      this.placeData.syncStatus$,
      this.seriesData.syncStatus$,
      this.seriesMapData.syncStatus$,
      this.userData.syncStatus$,
    ].forEach((api) =>
      api.subscribe(() => {
        const now = new Date();
        this.lastSyncAttempt$.next(now);
      }),
    );

    this.syncStatus$ = combineLatest([
      this.layoutData.syncStatus$,
      this.mapData.syncStatus$,
      this.placeData.syncStatus$,
      this.userData.syncStatus$,
      this.seriesData.syncStatus$,
      this.seriesMapData.syncStatus$,
    ]).pipe(
      // all are the same status otherwise return 'synchronizing'
      map((apis) => {
        let currentStatus = SyncStatusStates.synchronizing;
        if (apis.every((status) => status === apis[0])) {
          currentStatus = apis[0];
        }
        if (currentStatus === SyncStatusStates.synchronized) {
          const now = new Date();
          this.lastSyncSuccess$.next(now);
          this.wasLoaded = true;
        }
        return currentStatus;
      }),
      distinctUntilChanged(),
    );
  }
}
